import type { Location } from "history";
import type { ReactNode } from "react";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { isReactDomHydrating, navigationActions } from "@gamedev-web/gamedev-sdk-core";

import { LocationTrackerContext, NavigationDirection, PreviousLocation } from "../../helpers";

export type LocationTrackerProps = Readonly<{
  children: ReactNode;
}>;

export const LocationTracker = (props: LocationTrackerProps) => {
  const history = useHistory();
  const currentLocation = useLocation<Location>();
  const dispatch = useDispatch();

  const isHydrating = useSelector(isReactDomHydrating);
  const lastLocationProcessedForDispatch = useRef<Location | undefined>();

  useEffect(() => {
    if (lastLocationProcessedForDispatch.current !== currentLocation && !isHydrating) {
      dispatch(
        navigationActions.navigateToPath(currentLocation, lastLocationProcessedForDispatch.current)
      );
    }

    lastLocationProcessedForDispatch.current = currentLocation;
  }, [currentLocation, dispatch, isHydrating]);

  const lastTrackedLocationRef = useRef<Location | undefined>();
  const lastPreviousLocationRef = useRef<PreviousLocation>({ direction: NavigationDirection.New });

  let direction: NavigationDirection | undefined;
  let previousLocation: PreviousLocation;
  if (currentLocation === lastTrackedLocationRef.current) {
    previousLocation = lastPreviousLocationRef.current;
  } else {
    if (!lastTrackedLocationRef.current) {
      // first ever tracked location
      direction = NavigationDirection.New;
    } else {
      switch (history.action) {
        case "PUSH":
          direction = NavigationDirection.Forward;
          break;

        case "POP":
          direction = NavigationDirection.Back;
          break;

        case "REPLACE":
          direction = NavigationDirection.Replace;
          break;
      }
    }

    previousLocation = {
      location: lastTrackedLocationRef.current,
      direction,
    };

    // update lastTrackedLocationRef with current location only if there was actually a change
    lastTrackedLocationRef.current = currentLocation;
    lastPreviousLocationRef.current = previousLocation;
  }

  return (
    <LocationTrackerContext.Provider value={previousLocation}>
      {props.children}
    </LocationTrackerContext.Provider>
  );
};
